import React, { useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Header } from 'common/components/Header/Header';
import { ERoutesCommon, ERoutesPrivate, ERoutesPublic } from 'common/models/routesModel';
import { PermissionLayout } from 'common/components/Layouts/PermissionLayout';
import { getStoreShortId } from 'common/helpers/Store.helper';
import { CenterLayout } from 'common/components/Layouts/CenterLayout';
import { ScanPage } from 'app/pages/ScanPage';
import { RootPage } from 'app/pages/RootPage';
import { PrivacyPolicyPage } from 'app/pages/PrivacyPolicyPage';
import { TosPage } from 'app/pages/TosPage';
import { SocialAuthPage } from 'app/pages/SocialAuthPage';
import { CookieBlockedPage } from 'app/pages/CookieBlockedPage';
import { MainPage } from 'app/pages/MainPage';
import { TechnicalWorkPage } from 'app/pages/TechnicalWorkPage';
import { OrderHistoryPage } from 'app/pages/OrderHistoryPage';
import { RateStorePage } from 'app/pages/RateStorePage';
import { PersonalPromoPage } from 'app/pages/PersonalPromoPage';
import { BitecoinPage } from 'app/pages/BitecoinPage';
import { BitecoinHistoryPage } from 'app/pages/BitecoinHistoryPage';
import '@mb/mb-api';
import { useAppDispatch } from 'app/store/store.hooks';
import { initAuthModel } from 'app/store/reducers/auth.reducer';
import { getStoreModelByShortId, initStoreModel } from 'app/store/reducers/store.reducer';
import { ForgotPasswordPage, PasswordRestorePage } from 'entities/Auth/components';
import { LoginPage } from 'entities/Auth/components/LoginPage';
import { EmailConfirm } from 'entities/Auth/components/EmailConfirm';
import { OrderPage } from 'entities/Orders/components/OrderPage';
import { SettingsPage } from 'entities/User/components/SettingsPage';
import { SignupPage } from 'entities/Auth/components/SignupPage';
import { BagPage } from 'entities/Orders/components/BagPage';
import { AuthModal } from 'entities/UI/components/AuthModal';
import { ReferralRewardPopup } from 'entities/Referral/components/ReferralRewardPopup';
import { CompleteRegistrationPage } from 'entities/Auth/components/CompleteRegistrationPage';
import { useApplyPendingPromoCode } from 'entities/Promo/PendingPromoCode.hooks';

export const App: React.FC = () => {
  const dispatch = useAppDispatch();

  // Captures ?promoCode= deep links and applies any parked promo code once the user is authorized.
  useApplyPendingPromoCode();

  useEffect(() => {
    dispatch(initAuthModel());

    const storeShortId = getStoreShortId();

    if (storeShortId) {
      dispatch(getStoreModelByShortId(storeShortId));
    } else {
      dispatch(initStoreModel());
    }
  }, []);

  return (
    <Routes>
      <Route path={ERoutesCommon.TechnicalWork} element={<TechnicalWorkPage />} />
      <Route path={ERoutesCommon.CookieBlocked} element={<CookieBlockedPage />} />

      <Route
        path="*"
        element={
          <>
            <Header />

            <CenterLayout>
              <PermissionLayout
                publicRoutes={
                  <Routes>
                    <Route path={ERoutesCommon.PasswordForgot} element={<ForgotPasswordPage />} />
                    <Route path={ERoutesCommon.PasswordRestore} element={<PasswordRestorePage />} />
                    <Route path={ERoutesCommon.EmailConfirm} element={<EmailConfirm />} />
                    <Route path={ERoutesPublic.Login} element={<LoginPage />} />
                    <Route path={ERoutesPublic.Signup} element={<SignupPage />} />
                    <Route path={ERoutesPublic.SocialAuth} element={<SocialAuthPage />} />
                    <Route path={ERoutesCommon.Privacy} element={<PrivacyPolicyPage />} />
                    <Route path={ERoutesCommon.ToS} element={<TosPage />} />
                    <Route path={ERoutesCommon.Main} element={<MainPage />} />
                    <Route path={ERoutesCommon.ScanPage} element={<ScanPage />} />
                    <Route path="*" element={<RootPage />} />
                  </Routes>
                }
                privateRoutes={
                  <Routes>
                    <Route path={ERoutesCommon.PasswordRestore} element={<PasswordRestorePage />} />
                    <Route path={ERoutesCommon.EmailConfirm} element={<EmailConfirm />} />
                    <Route path={ERoutesPrivate.Order} element={<OrderPage />} />
                    <Route path={ERoutesPrivate.Bag} element={<BagPage />} />
                    <Route path={ERoutesPrivate.Settings} element={<SettingsPage />} />
                    <Route path={ERoutesPrivate.OrderHistory} element={<OrderHistoryPage />} />
                    <Route path={`${ERoutesPrivate.RateStore}/:id`} element={<RateStorePage />} />
                    <Route path={ERoutesCommon.Privacy} element={<PrivacyPolicyPage />} />
                    <Route path={ERoutesCommon.ToS} element={<TosPage />} />
                    <Route path={ERoutesCommon.Main} element={<MainPage />} />
                    <Route path={ERoutesCommon.ScanPage} element={<ScanPage />} />
                    <Route path={ERoutesPrivate.PersonalPromo} element={<PersonalPromoPage />} />
                    <Route path={ERoutesPrivate.Bitecoin} element={<BitecoinPage />} />
                    <Route path={ERoutesPrivate.BitecoinHistory} element={<BitecoinHistoryPage />} />
                    <Route path="*" element={<RootPage />} />
                  </Routes>
                }
                nonCompletedUserRoutes={
                  <Routes>
                    <Route path={ERoutesPrivate.CompleteRegistration} element={<CompleteRegistrationPage />} />
                    <Route path="*" element={<Navigate to={ERoutesPrivate.CompleteRegistration} />} />
                  </Routes>
                }
              />
            </CenterLayout>
            <AuthModal />
            <ReferralRewardPopup />
          </>
        }
      />
    </Routes>
  );
};
